import { useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';

/**
 * Bewerk-dialog voor een bestaand service-abonnement (fase 1.5): naam,
 * interval, volgende datum, korting, klant-mails, planning-opmerkingen en
 * einddatum via PATCH /api/abonnementen; plus een aparte "Extra wissel"-
 * sectie (POST ?action=extra-wissel) voor een eenmalige tussentijdse wissel.
 * Volgt de dialog-structuur van AbonnementDialog.
 */
export interface BewerkbaarAbonnement {
  id: string;
  naam: string;
  interval_weeks: number;
  next_run_at: string;
  korting_percentage: number | null;
  bevestigingen_sturen: boolean;
  opmerkingen_planning?: string | null;
  eindigt_op?: string | null;
}

interface AbonnementBewerkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  abonnement: BewerkbaarAbonnement;
  onSaved: () => void;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  return headers;
}

export function AbonnementBewerkDialog({ isOpen, onClose, abonnement, onSaved }: AbonnementBewerkDialogProps) {
  const [naam, setNaam] = useState(abonnement.naam);
  const [intervalWeeks, setIntervalWeeks] = useState(String(abonnement.interval_weeks));
  const [nextRunAt, setNextRunAt] = useState(abonnement.next_run_at);
  const [korting, setKorting] = useState(
    abonnement.korting_percentage != null ? String(abonnement.korting_percentage) : ''
  );
  const [mailsSturen, setMailsSturen] = useState(abonnement.bevestigingen_sturen);
  const [opmerkingen, setOpmerkingen] = useState(abonnement.opmerkingen_planning ?? '');
  const [eindigtOp, setEindigtOp] = useState(abonnement.eindigt_op ?? '');
  const [extraDatum, setExtraDatum] = useState('');
  const [ritmeVerschuiven, setRitmeVerschuiven] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [extraSubmitting, setExtraSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [melding, setMelding] = useState<string | null>(null);

  if (!isOpen) return null;

  const canSubmit =
    !!naam.trim() && !!nextRunAt &&
    Number.isInteger(Number(intervalWeeks)) && Number(intervalWeeks) >= 1 && Number(intervalWeeks) <= 52 &&
    !submitting && !extraSubmitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setMelding(null);
    try {
      const kortingRaw = korting.trim().replace(',', '.');
      const kortingParsed = kortingRaw ? Number.parseFloat(kortingRaw) : NaN;
      const headers = await authHeaders();
      const res = await fetch(`/api/abonnementen?id=${encodeURIComponent(abonnement.id)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          naam: naam.trim(),
          intervalWeeks: Number(intervalWeeks),
          nextRunAt,
          kortingPercentage: Number.isFinite(kortingParsed) && kortingParsed > 0 ? kortingParsed : null,
          bevestigingenSturen: mailsSturen,
          opmerkingenPlanning: opmerkingen.trim() || null,
          eindigtOp: eindigtOp || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.error || 'Bijwerken mislukt');
        return;
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtraWissel = async () => {
    if (!extraDatum) return;
    if (
      !window.confirm(
        `Extra wissel op ${extraDatum} inplannen? Dit maakt direct een order aan; de wissel wordt automatisch ingepland.${ritmeVerschuiven ? ' Het ritme verschuift vanaf deze wissel.' : ' Het reguliere ritme blijft ongewijzigd.'}`
      )
    ) {
      return;
    }
    setExtraSubmitting(true);
    setError(null);
    setMelding(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `/api/abonnementen?id=${encodeURIComponent(abonnement.id)}&action=extra-wissel`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ datum: extraDatum, ritmeVerschuiven }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.error || 'Extra wissel inplannen mislukt');
        return;
      }
      setMelding(
        data.melding ||
          `Extra wissel van ${extraDatum} is aangemaakt en wordt automatisch ingepland.`
      );
      setExtraDatum('');
      setRitmeVerschuiven(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setExtraSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-slate-900">Abonnement bewerken</h2>
          <p className="text-xs text-slate-500 mt-1">{abonnement.naam}</p>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Naam</span>
            <input
              value={naam}
              onChange={(e) => setNaam(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Elke … weken</span>
              <input
                type="number"
                min="1"
                max="52"
                value={intervalWeeks}
                onChange={(e) => setIntervalWeeks(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Volgende datum</span>
              <input
                type="date"
                value={nextRunAt}
                onChange={(e) => setNextRunAt(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
              <span className="text-[11px] text-slate-500">
                Ritme wijzigen ("vanaf september 2-wekelijks") = interval + deze datum samen aanpassen.
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3 items-start">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Korting %</span>
              <input
                type="text"
                inputMode="decimal"
                value={korting}
                onChange={(e) => setKorting(e.target.value)}
                placeholder="0"
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Einddatum</span>
              <input
                type="date"
                value={eindigtOp}
                onChange={(e) => setEindigtOp(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
              <span className="text-[11px] text-slate-500">Leeg = doorlopend; erna sluit het abonnement zichzelf af.</span>
            </label>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={mailsSturen}
                onChange={(e) => setMailsSturen(e.target.checked)}
              />
              <span className="text-sm text-slate-700">Klant-mails sturen</span>
            </label>
            <p className="text-xs text-slate-500 mt-1">
              Werkt alleen vooruit: geldt vanaf de eerstvolgende cyclus. Voor een al aangemaakte
              order pas je de bevestigingen aan via de order-acties op de klantpagina.
            </p>
          </div>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Instructie voor planning</span>
            <textarea
              rows={3}
              value={opmerkingen}
              onChange={(e) => setOpmerkingen(e.target.value)}
              maxLength={1000}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>

          <section className="border border-slate-200 rounded px-3 py-2 bg-slate-50">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Extra wissel</h3>
            <p className="text-xs text-slate-500 mb-2">
              Eenmalige tussentijdse wissel op klantverzoek. Maakt direct een order aan en wordt
              automatisch ingepland; het reguliere ritme blijft staan tenzij je het verschuift.
            </p>
            <div className="flex items-end gap-3 flex-wrap">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gewenste datum</span>
                <input
                  type="date"
                  value={extraDatum}
                  onChange={(e) => setExtraDatum(e.target.value)}
                  className="mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                />
              </label>
              <label className="flex items-center gap-2 pb-1.5">
                <input
                  type="checkbox"
                  checked={ritmeVerschuiven}
                  onChange={(e) => setRitmeVerschuiven(e.target.checked)}
                />
                <span className="text-sm text-slate-700">Ritme verschuiven vanaf deze wissel</span>
              </label>
              <button
                type="button"
                onClick={handleExtraWissel}
                disabled={!extraDatum || extraSubmitting || submitting}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded',
                  extraDatum && !extraSubmitting && !submitting
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                )}
              >
                {extraSubmitting ? 'Inplannen…' : 'Extra wissel inplannen'}
              </button>
            </div>
          </section>

          {melding && (
            <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{melding}</div>
          )}
          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting || extraSubmitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              canSubmit ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            )}
          >
            {submitting ? 'Opslaan…' : 'Wijzigingen opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
